import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

type Status = 'ok' | 'failed';
type ConnectionStatus = 'connected' | 'disconnected';

interface ServerStatus {
  status: Status;
  timestamp: string;
  database: {
    status: ConnectionStatus;
  }
  storage: {
    status: ConnectionStatus;
  }
  process: {
    uptime: string;
    responseTime: string;
    memory: {
      rss: string;
      heapTotal: string;
      heapUsed: string;
    }
  }
  system: {
    hostname: string;
    arch: string;
    memory: {
      total: string;
      free: string;
    }
    cpu: {
      loadAverage: number[];
      cores: number;
    }
    os: {
      platform: NodeJS.Platform;
      release: string;
      uptime: string;
    }
  }
}

export const allowedAuthMethods = ['username_password', 'api_key', 'public'] as const;
export type AllowedMethods = typeof allowedAuthMethods[number];

/**
 * Languages user-authored customization content is written in — POI type names, field
 * labels. Deliberately not called `locales`: this is unrelated to the app's interface
 * language, which comes from the localization CMS.
 */
export interface CustomizationLocales {
  /** Fallback locale. Every label is required to carry it, so nothing renders blank. */
  primary: string;
  enabled: string[];
}

export const defaultCustomizationLocales: CustomizationLocales = { primary: 'en', enabled: ['en'] };

interface Config {
  auth: {
    allowedMethods: AllowedMethods[];
    sessionExpiresInSeconds: number;
  };
  api: {
    version: number;
    versionString: string;
  };
  customizationLocales?: CustomizationLocales;
}

export const serverApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Not `/health`: that one is the public liveness probe the container calls, and it
    // answers with status and timestamp only. The detailed report requires `health:get`.
    getStatus: builder.query<ServerStatus, void>({
      query: () => '/health/details',
      onQueryStarted: handleApiResult(),
    }),
    getConfig: builder.query<Config, void>({
      query: () => '/config',
      providesTags: ['Config'],
      onQueryStarted: handleApiResult(),
    }),
    // `customizationLocales` is optional server-side, so a caller changing only
    // authentication keeps working.
    updateConfig: builder.mutation<void, Omit<Config, 'api'>>({
      query: (config) => ({ url: '/config', method: 'PUT', body: config }),
      invalidatesTags: ['Config'],
      onQueryStarted: handleApiResult('Api_ServerConfig_Update_Successful'),
    }),
  }),
});

export const { useGetStatusQuery, useGetConfigQuery, useUpdateConfigMutation } = serverApiSlice;
